import { QuoteRevision, RequestedPartDetail } from "@/lib/queries/types";
import { components } from "./ApiSchema";
import { components as allauthComponents } from "@/lib/AllauthApiSchema";
import { ErrorSchema } from "./queries/openapi";

export type FlatRequestedPartDetail = RequestedPartDetail & {
  level: number;
  lastChild: boolean;
};

export function flatParts(parts: RequestedPartDetail[], level: number = 0) {
  const partList: FlatRequestedPartDetail[] = [];

  parts.forEach((part, i) => {
    partList.push(
      { ...part, level, lastChild: i === parts.length - 1 },
      ...flatParts(part.children, level + 1),
    );
  });

  return partList;
}

export function readyToQuote(quoteRevision: QuoteRevision): boolean {
  const parts = flatParts(quoteRevision.requested_part_revisions);
  const draftCount = parts.reduce(
    (count, part) => count + (part.will_bid === null ? 1 : 0),
    0,
  );
  const willBidCount = parts.reduce(
    (count, part) => count + (part.will_bid === true ? 1 : 0),
    0,
  );
  return draftCount === 0 && willBidCount > 0;
}

export const quoteRevisionStatusLabel: Record<
  components["schemas"]["StatusEnum"],
  string
> = {
  new: "New",
  estimated: "Estimated",
  quote_created: "Quote Created",
  sent_quote: "Quote Sent",
  sent_no_bid: "No Bid",
  external_response: "External Response",
};

export function getPathnameSegments(pathname: string) {
  return pathname.split("/").filter((i) => i !== "");
}

export function formatNumber(
  number: number | string | null | undefined,
  isCurrency: boolean = false,
): string {
  if (number === null || number === undefined) return "";

  const n = Number(number);
  if (Number.isNaN(n)) {
    return number.toString();
  }
  const withLimitedFractionDigits = n.toFixed(2);

  return new Intl.NumberFormat("en-US", {
    maximumSignificantDigits: isCurrency ? 6 : 4,
  }).format(`${Number(withLimitedFractionDigits)}`);
}

export function setIn<T>(
  obj: T,
  keyPath: readonly PropertyKey[],
  value: unknown,
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let curr: any = obj;
  for (let i = 0; i < keyPath.length - 1; i++) {
    const key = keyPath[i];
    if (!(key in curr) || typeof curr[key] !== "object" || curr[key] === null) {
      curr[key] = {};
    }
    curr = curr[key];
  }
  curr[keyPath[keyPath.length - 1]] = value;
  return obj;
}

export function getIn(
  obj: Record<string, unknown> | null,
  keyPath: readonly PropertyKey[],
  defaultValue?: unknown,
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let curr: any = obj;
  for (const key of keyPath) {
    if (curr && typeof curr === "object" && key in curr) {
      curr = curr[key];
    } else {
      return defaultValue;
    }
  }
  return curr;
}

export function truncateTextMiddle(text: string, lengthLimit: number): string {
  if (text.length <= lengthLimit) return text;

  const ellipsis = "...";
  const middleIndex = Math.floor(text.length / 2);
  const truncatedLength = text.length - lengthLimit + ellipsis.length;
  const head = text.substring(0, middleIndex - Math.ceil(truncatedLength / 2));
  const tail = text.substring(middleIndex + Math.ceil(truncatedLength / 2));
  return `${head}...${tail}`;
}

export function equalArrays(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  const aSorted = [...a].sort();
  const bSorted = [...b].sort();
  return aSorted.every((v, i) => v === bSorted[i]);
}

export function canAccessSettings(
  user: components["schemas"]["LoggedInUserSchema"],
  teamSlug?: string,
) {
  const currentTeam = user.teams.find((team) => team.slug === teamSlug);
  return currentTeam?.is_admin || user.is_superuser;
}

export function getProviderName(
  provider:
    | components["schemas"]["SubscriptionListItemSchema"]["provider"]
    | allauthComponents["schemas"]["Provider"]["id"]
    | Pick<allauthComponents["schemas"]["Provider"], "id" | "name">,
): string {
  const providerId = typeof provider === "string" ? provider : provider.id;
  const providerName = typeof provider === "string" ? provider : provider.name;

  switch (providerId.toLowerCase()) {
    case "microsoft":
      return "Microsoft";
    case "microsoft_email":
      return "Microsoft Outlook";
    case "custom":
      return "Custom";
    default:
      return providerName;
  }
}

export function getErrorMessage(error: ErrorSchema): string {
  if (Array.isArray(error.detail)) {
    return error.detail.map((e) => e.msg).join("\n");
  }
  return String(error.detail);
}
